import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import { 
  Building2, AlertCircle, Sparkles, BookOpen, 
  ArrowRight, Award, Laptop, Droplets, Camera, Bus
} from 'lucide-react';
import StructuredData from '../components/StructuredData';
import '../styles/TheSchool.scss';

export default function TheSchool() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const { t } = useTranslation();

  const navItems = [
    { id: 'rules-regulations', label: t('nav_rules') },
    { id: 'message-students', label: t('nav_msg_students') },
    { id: 'message-parents', label: t('nav_msg_parents') },
    { id: 'infrastructure', label: t('nav_infrastructure') },
    { id: 'teaching-staff', label: t('nav_teaching_faculty') },
    { id: 'school-management', label: t('nav_school_management') },
  ];

  const rules = [
    {
      title: 'Mandatory Attendance & Leave',
      desc: 'Students must maintain a minimum of 75% attendance during the academic session. In case of illness or emergency absence, a written leave application signed by the parent must be submitted promptly.'
    },
    {
      title: 'Punctuality & Assembly Decor',
      desc: 'School gates close 10 minutes after the morning bell. Latecomers will not be admitted to class without the Principal’s endorsement. Active, respectful participation in morning assembly is compulsory.'
    },
    {
      title: 'Strict Ban on Electronic Gadgets',
      desc: 'Mobile phones, smartwatches, digital games, and unauthorized electronic devices are strictly prohibited on the school premises. Confiscated devices will only be returned to parents at the term end.'
    },
    {
      title: 'Dress Code & Grooming Norms',
      desc: 'Students must strictly follow the prescribed daily uniform schedule (regular uniform vs house dress). Uniforms must be clean, neat, and pressed with polished black shoes and school ID cards worn.'
    },
    {
      title: 'Zero Tolerance for Bullying',
      desc: 'Every student has the right to a safe, respectful environment. Teasing, ragging, abusive language, or aggressive behavior will lead to strict disciplinary action, including suspension.'
    },
    {
      title: 'Campus Cleanliness & Property Care',
      desc: 'Defacing school walls, damaging laboratory apparatus, library books, or classroom furniture is strictly forbidden. Students are encouraged to maintain a green, litter-free campus.'
    }
  ];

  const infraItems = [
    {
      title: 'Well-Ventilated Classrooms',
      desc: 'Over 35 spacious, brightly lit classrooms designed with comfortable ergonomic seating and modern whiteboards.',
      icon: <Building2 size={24} />
    },
    {
      title: 'High-Tech Computer Laboratory',
      desc: 'Equipped with modern networked PCs, high-speed fiber internet, and programming software for coding classes.',
      icon: <Laptop size={24} />
    },
    {
      title: 'Composite Science Laboratory',
      desc: 'Fully stocked laboratory with apparatus, models, and reagents for practical exploration of Physics, Chemistry, and Biology.',
      icon: <Sparkles size={24} />
    },
    {
      title: 'Knowledge Central Library',
      desc: 'Repository of 3,500+ curated volumes, children’s literature, NCERT references, daily newspapers, and periodicals.',
      icon: <BookOpen size={24} />
    },
    {
      title: 'Spacious Sports Field & Court',
      desc: 'Multi-sport outdoor grounds for Football, Cricket, Badminton, Volleyball, and athletics, fostering teamwork.',
      icon: <Award size={24} />
    },
    {
      title: 'Multi-Stage RO Drinking Water',
      desc: 'Heavy-duty industrial RO filtration plants providing continuous, tested potable safe drinking water across campus.',
      icon: <Droplets size={24} />
    },
    {
      title: '24/7 CCTV Campus Surveillance',
      desc: 'Complete high-definition CCTV coverage monitoring all corridors, gates, and play zones for student safety.',
      icon: <Camera size={24} />
    },
    {
      title: 'Safe Fleet Transportation',
      desc: 'GPS-tracked dedicated school buses and vans with trained drivers and attendants serving routes within a 10 km radius.',
      icon: <Bus size={24} />
    }
  ];

  return (
    <div className="the-school-page">
      <StructuredData type="organization" />
      <Helmet>
        <title>The School - Prabhat Tara Vidya Niketan Goraul</title>
        <meta
          name="description"
          content="Explore The School at Prabhat Tara Vidya Niketan: Rules & regulations, Messages for students and parents, Infrastructure, Teaching staff, and School management."
        />
        <link rel="canonical" href="https://prabhattaravidyaniketan.in/the-school" />
      </Helmet>

      {/* Hero Banner */}
      <div className="hero-banner">
        <span className="badge">
          <Building2 size={16} /> Campus Life, Values & Infrastructure
        </span>
        <h1>The School – Prabhat Tara Vidya Niketan</h1>
        <p>
          Discover our guiding principles, student & parent partnerships, world-class physical campus infrastructure, and visionary management.
        </p>
      </div>

      {/* Nav Pills */}
      <div className="nav-pills-bar">
        <div className="nav-container">
          {navItems.map((item) => (
            <a key={item.id} href={`#${item.id}`}>
              {item.label}
            </a>
          ))}
        </div>
      </div>

      <div className="main-content">
        
        {/* 1. RULES & REGULATIONS */}
        <section id="rules-regulations" className="school-card">
          <span className="card-tag">1. Code of Discipline</span>
          <h2>School Rules & Regulations</h2>
          <p className="card-desc">
            Discipline is the bridge between goals and accomplishment. All students admitted to Prabhat Tara Vidya Niketan are required to adhere strictly to our code of conduct.
          </p>

          <div className="rules-grid">
            {rules.map((r, idx) => (
              <div key={idx} className="rule-item">
                <h4>{r.title}</h4>
                <p>{r.desc}</p>
              </div>
            ))}
          </div>

          <div className="rule-notice-banner">
            <AlertCircle size={22} />
            <p>
              <strong>Notice:</strong> Habitual defiance of rules, unpunctuality, or damage to school property will warrant immediate notification of parents and potential disciplinary review.
            </p>
          </div>
        </section>

        {/* 2. MESSAGE FOR STUDENTS */}
        <section id="message-students" className="school-card">
          <span className="card-tag">2. Words of Inspiration</span>
          <h2>A Message for Our Beloved Students</h2>
          <p className="card-desc">
            From the faculty and mentors of Prabhat Tara Vidya Niketan to every young scholar stepping through our gates.
          </p>

          <div className="message-box">
            <span className="quote-mark">“</span>
            <h3>Dear Students, The Future Belongs to Those Who Prepare Today</h3>
            <p>
              Welcome to an environment crafted to ignite your curiosity and bring your highest potential to fruition. Remember that talent is merely the seed; discipline, relentless practice, and honesty are the water and sunlight that transform it into towering achievement.
            </p>
            <p>
              Do not be afraid of making mistakes in the classroom or laboratory—every error is a stepping stone to deeper wisdom. Cultivate the habit of asking questions, reading books beyond your syllabus, helping your peers, and respecting your elders.
            </p>
            <p style={{ margin: 0, fontWeight: '600', color: '#011673' }}>
              "Work hard in silence; let your success make the noise." Be proud ambassadors of Prabhat Tara Vidya Niketan!
            </p>
          </div>
        </section>

        {/* 3. MESSAGE FOR PARENTS */}
        <section id="message-parents" className="school-card">
          <span className="card-tag">3. Partnership in Growth</span>
          <h2>A Message for Parents & Guardians</h2>
          <p className="card-desc">
            Parenting and education are two sides of the same sacred coin. Together, we form an unbreakable support system for your child.
          </p>

          <div className="message-box parents-message">
            <span className="quote-mark">“</span>
            <h3>Dear Parents, You Are Your Child’s First and Most Enduring Teachers</h3>
            <p>
              We consider parents our most vital partners in education. While the school imparts academic knowledge, scientific skills, and structured social habits, your home provides the emotional bedrock of love, reassurance, and values.
            </p>
            <p>
              We encourage you to:
            </p>
            <ul>
              <li>Check your child’s school diary every evening for teacher remarks and homework assignments.</li>
              <li>Spend at least 30 minutes in conversation about their school day without digital distractions.</li>
              <li>Attend Parent-Teacher Meetings (PTMs) regularly to celebrate milestones and address challenges early.</li>
              <li>Encourage reading habits, healthy eating, adequate sleep, and physical play outdoors.</li>
            </ul>
            <p className="message-footer">
              Our doors are always open for your valuable feedback, constructive suggestions, and mutual dialogue.
            </p>
          </div>
        </section>

        {/* 4. SCHOOL INFRASTRUCTURE */}
        <section id="infrastructure" className="school-card">
          <span className="card-tag">4. Physical Environment</span>
          <h2>Modern Campus Infrastructure</h2>
          <p className="card-desc">
            Spanning over 8,000 square meters of tranquil green surroundings in Rajkhand, Goraul, PTVN boasts comprehensive infrastructure to support holistic learning.
          </p>

          <div className="infra-grid">
            {infraItems.map((item, idx) => (
              <div key={idx} className="infra-card">
                <div className="icon-box">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="infra-cta-wrap">
            <Link to="/about-us#facilities">
              Read More About Our Facilities & Safety Measures <ArrowRight size={18} />
            </Link>
          </div>
        </section>

        {/* 5. TEACHING STAFF & FACULTY */}
        <section id="teaching-staff" className="school-card">
          <span className="card-tag">5. Our Mentors</span>
          <h2>Teaching Staff & Academic Faculty</h2>
          <p className="card-desc">
            The heartbeat of Prabhat Tara Vidya Niketan is our team of over 50 passionate, highly qualified, and child-centered educators.
          </p>

          <div className="teaching-faculty-preview">
            <div className="faculty-copy">
              <p>
                Our faculty members hold advanced degrees (Post Graduates, B.Ed., and certified specialist credentials) and undergo continuous professional pedagogical training to stay at the vanguard of modern education. With a healthy teacher-student ratio of 1:1.5 per section, every student receives personalized mentorship, encouragement, and remedial assistance.
              </p>
              <div className="faculty-actions">
                <Link to="/teaching-staff" className="btn-primary">
                  Browse Complete Teaching Staff Directory <ArrowRight size={16} />
                </Link>
                <Link to="/job-enquiry" className="btn-secondary">
                  Join Our Faculty / Career <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            <div className="faculty-media">
              <img 
                src="/descriptive-image/students-learning-with-laptops-classroom.png" 
                alt="Faculty and Students in Digital Classroom" 
              />
            </div>
          </div>
        </section>

        {/* 6. SCHOOL MANAGEMENT */}
        <section id="school-management" className="school-card">
          <span className="card-tag">6. Visionary Governance</span>
          <h2>School Management & Administration</h2>
          <p className="card-desc">
            Guided by decades of dedicated public service and commitment to affordable quality education in Bihar.
          </p>

          <div className="management-preview-grid">
            <div className="management-card">
              <img 
                src="/images/director.png" 
                alt="Director Mr. Shubh Nath Singh" 
              />
              <h3>Mr. Shubh Nath Singh</h3>
              <p className="role-tag">Director & Founder Patron</p>
              <p>
                Leading the institution since 1995 with unwavering commitment to moral integrity, infrastructural growth, and student welfare.
              </p>
              <Link to="/director-message">
                Read Director's Message <ArrowRight size={14} />
              </Link>
            </div>

            <div className="management-card">
              <img 
                src="/images/principal.png" 
                alt="Principal Mr. Kishan" 
              />
              <h3>Mr. Kishan</h3>
              <p className="role-tag">Principal & Academic Head</p>
              <p>
                Ensuring rigorous pedagogical execution, ethical discipline, and holistic talent development across all classes.
              </p>
              <Link to="/principal-message">
                Read Principal's Message <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}

